import React from 'react';

import {NotificationActions} from '../../services/notification';
import CaseResolutionButtons from './CaseResolutionButtons';
import { type ElapsedTimeLabels, useRelativeTime } from './hooks/useRelativeTime';
import NotificationBodyText from './NotificationBodyText';
export type NotificationType =
    | 'CaseAutoClosed'
    | 'CaseClosed'
    | 'CaseFirstResponseProvided'
    | 'CaseMyActionOrPendingCustomerResponse'
    | 'CaseNew'
    | 'CaseResearchOrDevelopmentOrProductManagement'
    | 'CaseResolved'
    | 'CaseRootCauseFoundOrSolutionInProgress'
    | 'CaseTempSolutionOrWorkaroundProvided'
    | 'CaseUnderInvestigation'
    | 'AgreementCloseToExpirationDate'
    | 'AgreementContactUpdated'
    | 'AgreementExtended'
    | 'AssetLifecycleStatusChanged';

export type NotificationData = Record<string, string | number | boolean | null |undefined>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NotificationTextsShape = Record<string, Record<string, any>>;

export type { NotificationTextsShape };

export type { ElapsedTimeLabels };

interface NotificationContentRenderProps {
    notificationType: NotificationType;
    data: NotificationData;
    createdAt: string;
    onMarkAsRead?: () => void;
    notificationTexts?: NotificationTextsShape;
    actions?: NotificationActions;
    contactUsLink?: string;
    elapsedTimeLabels?: ElapsedTimeLabels;
}

const PLACEHOLDER_MAP: Record<string, string> = {
    agreementNumber: 'number',
};

const DATE_PLACEHOLDER_KEYS = new Set(['modifiedAt']);

function resolvePlaceholders(text: string, data: NotificationData): string {
    return text.replace(/\{\{(\w+)\}\}/g, (_, placeholder) => {
        const dataKey = PLACEHOLDER_MAP[placeholder] ?? placeholder;
        const rawValue = data?.[dataKey];
        if (rawValue == null) return `{{${placeholder}}}`;
        if (DATE_PLACEHOLDER_KEYS.has(placeholder)) return formatDate(String(rawValue));
        return String(rawValue);
    });
}

const formatDate = (date?: string ) => {
    return new Date(date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
}

const ContactUsLink = ({ contactUsLink, label }: { contactUsLink?: string; label: string }) => (
    <a href={contactUsLink} target="_blank" rel="noopener noreferrer" style={{ color: '#c00', fontWeight: 'bold', textDecoration: 'none' }}>
        {label}
    </a>
);

const NotificationContentRender = ({
    data,
    createdAt,
    notificationType,
    onMarkAsRead,
    notificationTexts,
    actions,
    contactUsLink,
    elapsedTimeLabels,
}: NotificationContentRenderProps) => {
    const relativeTime = useRelativeTime(createdAt, elapsedTimeLabels);
    const resolvedTexts = (notificationTexts && !Array.isArray(notificationTexts) && 'cases' in notificationTexts)
        ? notificationTexts : null;
    const { cases, agreements, assets } = resolvedTexts || {};
    const contactUsLabel = (resolvedTexts?.['contact-us'] as unknown as string) ?? 'Contact Us';
    // Resolves {{placeholder}} tokens in a text string against notification data
    const t = (text: string) => resolvePlaceholders(text, data);
    const str = (val: unknown) => {
        if (val === null || val === undefined) {
            return '';
        }
        if (typeof val === 'object') {
            return JSON.stringify(val);
        }
        return val as string;
    };
    const renderTitle = (): string => {
        switch (notificationType) {
            case 'CaseNew':                                      return t(cases['new'].title);
            case 'CaseFirstResponseProvided':                    return t(cases['first-response-provided'].title);
            case 'CaseUnderInvestigation':                       return t(cases['under-investigation'].title);
            case 'CaseMyActionOrPendingCustomerResponse':        return t(cases['my-action'].title);
            case 'CaseTempSolutionOrWorkaroundProvided':         return t(cases['temp-solution'].title);
            case 'CaseResearchOrDevelopmentOrProductManagement': return t(cases['product-management'].title);
            case 'CaseRootCauseFoundOrSolutionInProgress':       return t(cases['root-cause-found'].title);
            case 'CaseResolved':                                 return t(cases['resolved'].title);
            case 'CaseClosed':                                   return t(cases['closed'].title);
            case 'CaseAutoClosed':                               return t(cases['auto-closed'].title);
            case 'AgreementCloseToExpirationDate':               return t(agreements['close-to-expiration'].title);
            case 'AgreementContactUpdated':                      return t(agreements['contact-updated'].title);
            case 'AgreementExtended':                            return t(agreements['extended'].title);
            case 'AssetLifecycleStatusChanged':                  return t(assets['lifecycle-status-changed'].title);
            default:                                             return 'New notification';
        }
    };

    const renderBody = (): React.ReactNode => {
        switch (notificationType) {
            case 'CaseNew':
                return (
                    <>
                        <NotificationBodyText>{t(cases?.subject)}</NotificationBodyText>
                        <NotificationBodyText bold>{t(cases['new']['body-1'])}</NotificationBodyText>
                    </>
                );

            case 'CaseFirstResponseProvided':
                return (
                    <>
                        <NotificationBodyText>{t(cases?.subject)}</NotificationBodyText>
                        <NotificationBodyText bold>{t(cases['first-response-provided']['body-1'])}</NotificationBodyText>
                    </>
                );

            case 'CaseUnderInvestigation':
                return (
                    <>
                        <NotificationBodyText>{t(cases.subject)}</NotificationBodyText>
                        <NotificationBodyText bold>{t(cases['under-investigation']['body-1'])}</NotificationBodyText>
                    </>
                );

            case 'CaseTempSolutionOrWorkaroundProvided':
            case 'CaseRootCauseFoundOrSolutionInProgress':
            case 'CaseResearchOrDevelopmentOrProductManagement':
                return (
                    <NotificationBodyText>{t(cases.subject)}</NotificationBodyText>
                );

            case 'CaseMyActionOrPendingCustomerResponse': {
                const c = cases['my-action'];
                return (
                    <>
                        <NotificationBodyText>{t(cases.subject)}</NotificationBodyText>
                        <NotificationBodyText>
                            {t(c['body-1-0'])}{' '}
                            {t(c['body-1-1'])}{' '}
                            <ContactUsLink contactUsLink={contactUsLink} label={contactUsLabel} />{' '}
                            {t(c['body-1-2'])}
                        </NotificationBodyText>
                        <NotificationBodyText  bold>{t(c['body-2'])}</NotificationBodyText>
                    </>
                );
            }

            case 'CaseResolved': {
                const c = cases['resolved'];
                const acceptAction = actions?.find(a => a.type === 'AcceptCaseResolution');
                const rejectAction = actions?.find(a => a.type === 'RejectCaseResolution');
                return (
                    <>
                        <NotificationBodyText>{t(cases.subject)}</NotificationBodyText>
                        <NotificationBodyText bold>{t(c['body-1'])}</NotificationBodyText>
                        <CaseResolutionButtons
                            acceptDisabled={!(acceptAction?.isAvailable ?? true)}
                            rejectDisabled={!(rejectAction?.isAvailable ?? true)}
                            caseId={data?.caseId as string ?? null}
                            caseNumber={data?.caseNumber as string ?? null}
                            caseSubject={data?.subject as string ?? null}
                            onAccept={onMarkAsRead}
                        />
                        <NotificationBodyText bold>{t(c['body-2'])}</NotificationBodyText>
                    </>
                );
            }

            case 'CaseClosed': {
                const c = cases['closed'];
                return (
                    <>
                        <NotificationBodyText>{t(cases.subject)}</NotificationBodyText>
                        <NotificationBodyText>
                            {t(c['body-1-0'])} <strong>{t(c['body-1-1'])}</strong>
                        </NotificationBodyText>
                        <NotificationBodyText>
                            <strong>{t(c['body-2-1'])}{' '}
                            <ContactUsLink contactUsLink={contactUsLink} label={contactUsLabel} />{' '}
                            {t(c['body-2-2'])}</strong>
                        </NotificationBodyText>
                    </>
                );
            }

            case 'CaseAutoClosed': {
                const c = cases['auto-closed'];
                return (
                    <>
                        <NotificationBodyText>{t(cases.subject)}</NotificationBodyText>
                        <NotificationBodyText>
                            {t(c['body-1-0'])} <strong>{t(c['body-1-1'])}</strong> {t(c['body-1-2'])}
                        </NotificationBodyText>
                        <NotificationBodyText>
                            {t(c['body-2-1'])}{' '}
                            <ContactUsLink contactUsLink={contactUsLink} label={contactUsLabel}/>{' '}
                            {t(c['body-2-2'])}
                        </NotificationBodyText>
                    </>
                );
            }

            case 'AgreementCloseToExpirationDate': {
                const c = agreements['close-to-expiration'];
                return (
                    <>
                        <NotificationBodyText>
                            {t(c['body-1-0'])} <strong>{formatDate(String(data?.validTo))}</strong> {t(c['body-1-1'])}
                        </NotificationBodyText>
                        <NotificationBodyText>
                        {t(c['body-1-2'])}
                        </NotificationBodyText>
                    </>
                );
            }

            case 'AgreementContactUpdated': {
                const c = agreements['contact-updated'];
                const primaryContact = data?.primaryContact as {
                    firstName?:string;
                    lastName?:string;
                };
                const secondaryContact = data?.secondaryContact as {
                    firstName?:string;
                    lastName?:string;
                };
                return (
                    <>
                        <NotificationBodyText>
                            {t(c['body-1-0'])} <strong>{formatDate(String(data?.modifiedAt))} </strong>  {t(c['body-1-1'])}
                        </NotificationBodyText>
                        {primaryContact &&( <NotificationBodyText>
                            {t(c['body-2-0'])} <strong>{str(primaryContact?.firstName)} {str(primaryContact?.lastName)}</strong>
                        </NotificationBodyText>)}
                        {secondaryContact && (<NotificationBodyText>
                        {t(c['body-2-1'])} <strong>{str(secondaryContact?.firstName)} {str(secondaryContact?.lastName)}</strong>
                        </NotificationBodyText> ) }
                    </>
                );
            }

            case 'AgreementExtended': {
                const c = agreements['extended'];
                return (
                    <>
                        <NotificationBodyText>
                            {t(c['body-1-0'])} <strong>{formatDate(String(data?.validFrom))}  {t(c['body-1-1'])}</strong>
                        </NotificationBodyText>
                        <NotificationBodyText>
                            {t(c['body-2-0'])}<strong>{formatDate(String(data?.validTo))} {t(c['body-2-1'])}</strong>
                        </NotificationBodyText>
                    </>
                );
            }

            case 'AssetLifecycleStatusChanged': {
                const c = assets['lifecycle-status-changed'];
                return (
                    <NotificationBodyText>
                        {t(c['body-1-0'])}<strong> {data?.lifecycleStatus}</strong>{t(c['body-1-1'])} <strong> {formatDate(String(data?.modifiedAt))}  </strong> {t(c['body-1-2'])} {t(c['body-1-3'])}
                    </NotificationBodyText>
                );
            }

            default:
                return <NotificationBodyText>You have a new notification</NotificationBodyText>;
        }
    };

    return (
        <>
            <div className="cop-notification__header">
                <h4 className="cop-notification__title">{renderTitle()}</h4>
                <span className="cop-notification__time">{relativeTime}</span>
            </div>
            {renderBody()}
        </>
    );
};
export default NotificationContentRender;

